import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  InputLabel,
  FormControl,
  Container,
  Grid,
} from "@mui/material"
import { useAuthUser, useIsAuthenticated } from "react-auth-kit"
import api from "../../../../utils/api"

interface InstanceData {
  id: number
  eventId: any
  title: string
  description: string
  questionCount: number
  instanceNum: number
  status: string
}

function EditInstancePage() {
  const isAuthenticated = useIsAuthenticated()
  const auth = useAuthUser()
  const navigate = useNavigate()

  const [instances, setInstances] = useState([] as InstanceData[])
  const [username, setUsername] = useState("")
  const [description, setDescription] = useState<string>("")
  const [questionCount, setQuestionCount] = useState<number>(0)

  const [selectedInstanceId, setSelectedInstanceId] = useState("") // New state to store selected question

  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    isAuthenticated() ? setUsername(auth()?.username) : setUsername("")
  }, [isAuthenticated, auth])

  const fetchInstances = (eventId: string) => {
    //useEffect(() => {
    const fetchVotingInstances = async () => {
      // setIsLoading(true)
      try {
        const response = await api.get(
          `/admin/events/${eventId}/voting-instances`
        )

        setInstances(response.data)
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to get instances."
        console.error("Error fetching voting instances:", errorMessage)
      }
    }
    fetchVotingInstances()

    return { instances, setInstances }
  }

  // Handle question selection from dropdown
  const handleInstanceSelect = async (instanceId: any) => {
    setSelectedInstanceId(instanceId)
    // Fetch question data for editing based on the selected questionId
    try {
      const response = await api.get(`/admin/instances/${instanceId}`)

      const instanceData = response.data
      // Populate the state with question data
      setDescription(instanceData.description)
      setQuestionCount(instanceData.questionCount)
    } catch (error) {
      console.error("Error fetching instance data:", error)
    }
  }

  const handleSubmit = async () => {
    if (!description || !questionCount) {
      setErrorMessage("All fields are required")
      setOpenSnackbar(true)
      return
    }

    // Construct the updated instance data
    const updatedInstanceData = {
      description,
      questionCount,
    }

    try {
      await api.put(
        `/admin/instances/${selectedInstanceId}`,
        updatedInstanceData
      )

      navigate("/admin/dashboard", {
        state: { notification: "Instance edited successfully!" },
      })
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to edit instance."
      console.error("There was an error:", errorMessage)
    }
  }

  const handleSnackbarClose = () => {
    setOpenSnackbar(false)
    setErrorMessage("")
  }

  const eventRef = auth()?.eventId ?? ""
  const eventId = eventRef._path?.segments[1] ?? ""

  useEffect(() => {
    fetchInstances(eventId)
  })

  return (
    <>
      <Container component="main">
        <Typography variant="h6" m={4}>
          Edit a Voting Instance
        </Typography>
        <form onSubmit={(e) => e.preventDefault()}>
          {/* //if statment */}
          {instances && instances.length > 0 ? (
            <>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="instance-select-label">
                  Select a Instance
                </InputLabel>
                <Select
                  label="Select a Instance"
                  labelId="instance-select-label"
                  value={selectedInstanceId}
                  onChange={(e) => handleInstanceSelect(e.target.value)}
                >
                  {instances.some(
                    (instance) => instance.status === "pre-voting"
                  ) ? (
                    instances.map(
                      (instance) =>
                        instance.status === "pre-voting" && (
                          <MenuItem key={instance.id} value={instance.id}>
                            {instance.description}
                          </MenuItem>
                        )
                    )
                  ) : (
                    <MenuItem disabled>No instances available</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Description */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={description}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    required
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Grid>

                {/* Question Count */}
                <Grid item xs={12}>
                  <TextField
                    label="Question Count"
                    type="number"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    required
                    value={questionCount}
                    inputProps={{ min: 1 }}
                    onChange={(e) =>
                      setQuestionCount(parseInt(e.target.value, 10))
                    }
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    onClick={handleSubmit}
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography variant="body1">No instances available</Typography>
          )}
        </form>
      </Container>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default EditInstancePage
